import { OS } from 'types';

import Image from '~/components/Image';
import Tracked from '~/components/Tracked';
import useRootData from '~/hooks/useRootData';
import Icon from '~/legacy/components/Icon';

import { Dialog, DialogDismiss } from '../Dialog';
import { DownloadFromAppStoreLink, Stars } from '../DownloadAppBlock';

const APPS_FLYER_LINK = 'https://thesolesupplier.onelink.me/BPkz/r8mzidns';

type Props = {
  isOpen: boolean;
  onDismiss: () => void;
};

function DownloadAppView() {
  return (
    <div className="flex w-full items-center justify-center rounded-lg bg-grey-tile md:gap-6 md:px-4 md:py-2">
      <QrCode />
      <div className="gap-2 md:flex-col ">
        <div className="hidden md:block md:pb-2">
          <p className=" text-base">Scan the QR code to get started</p>
        </div>
        <span className="flex flex-col items-center justify-center gap-1 md:flex-row md:gap-4">
          <DownloadFromAppStoreLink
            pagePosition="Body"
            href={APPS_FLYER_LINK}
          />
          <div className="flex items-center gap-2">
            <Stars />
            <span>4.8</span>
          </div>
        </span>
      </div>
    </div>
  );
}

function QrCode() {
  return (
    <div className="hidden aspect-square w-1/4 md:block">
      <Image
        loading="lazy"
        alt="QR Code to download the app"
        src="https://cms-cdn.thesolesupplier.co.uk/bolt/assets/images/download_app_notify_me_qr_code.png"
      />
    </div>
  );
}

export const AppDownload = ({ isOpen, onDismiss }: Props) => {
  const { clientOs } = useRootData();
  const isAndroidClient = clientOs === OS.Android;

  return isOpen && !isAndroidClient ? (
    <Dialog
      onDismiss={onDismiss}
      isOpen={true}
      className="z-50 mx-4 flex w-full flex-col gap-4 rounded-lg bg-white px-6 pt-8 md:mx-0 md:w-[456px] lg:w-[552px]"
    >
      <div className="flex flex-col items-center justify-center gap-4 self-stretch rounded-lg">
        <div className="flex w-full flex-row gap-2">
          <span className="text-left font-heading text-[24px] font-normal leading-tight lg:text-[32px]">
            Get notifications faster with the app.
          </span>
          <span className="flex items-start self-stretch">
            <DialogDismiss>
              <Icon
                name="close"
                width="12"
                height="12"
                viewBox="457 457 878 878"
                fill="black"
              />
            </DialogDismiss>
          </span>
        </div>
        <div className=" flex w-full flex-col gap-4">
          <span className="self-stretch text-left text-sm font-normal">
            You've turned on emails, but for the best chance of copping,
            download our app. Receive instant stock alerts from 40+ retailers in
            the palm of your hand.
          </span>
          <div className="flex gap-3 bg-grey-tile">
            <div className="flex items-center justify-center gap-4 rounded-lg">
              <DownloadAppView />
            </div>
            <div className="flex w-4/5 md:hidden md:basis-full">
              <Tracked.ExternalLink
                name="DOWNLOAD_THE_APP"
                href={APPS_FLYER_LINK}
                pagePosition={'Modal'}
                target="_blank"
                rel="noreferrer"
                variant="forest"
                className="h-10 leading-tight"
              >
                Download the app
              </Tracked.ExternalLink>
            </div>
          </div>
          <div className="flex justify-center">
            <div>
              <Image
                className="h-64"
                alt="Get release reminders with The Sole Supplier iOS app"
                src="https://cms-cdn.thesolesupplier.co.uk/bolt/assets/images/app_release_notification_cropped.png"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  ) : null;
};
